import { createReducer, on } from '@ngrx/store';
import { PreventiviActions } from './preventivi.actions';
import {Preventivo, PreventiviQueryParams, DocumentoPreventivo} from '../../core/models/preventivi.model';
import {Capitolo} from '../../core/models/capitoli.model';

export interface PreventiviState {
  items: Preventivo[];
  capitoli: Capitolo[];
  capitoliLoading: boolean;
  loading: boolean;
  error: string | null;
  documenti: DocumentoPreventivo[];
  documentiLoading: boolean;
  totalElements: number;
  totalPages: number;
  pageIndex: number;
  pageSize: number;
  lastQuery: PreventiviQueryParams | undefined;

  selected: Preventivo | null;
  createSuccessCount: number;

  opLoading: boolean;
  opError: string | null;
}

export const initialState: PreventiviState = {
  items: [],
  capitoli: [],
  capitoliLoading: false,
  loading: false,
  error: null,

  documenti: [],
  documentiLoading: false,

  totalElements: 0,
  totalPages: 0,
  pageIndex: 0,
  pageSize: 20, // Swagger: default size = 20
  lastQuery: undefined,

  selected: null,
  createSuccessCount: 0,

  opLoading: false,
  opError: null,
};

// Helper per confronti sicuri tra ID
const toId = (v: unknown) => String(v ?? '');

export const preventiviReducer = createReducer(
  initialState,

  on(PreventiviActions.loadList, (s, { params }) => ({ ...s, loading: true, error: null, lastQuery: params })),
  on(PreventiviActions.loadListSuccess, (s, { items, totalElements, totalPages, pageIndex }) => ({
    ...s,
    loading: false,
    items,
    totalElements,
    totalPages,
    pageIndex,
  })),
  on(PreventiviActions.loadListFailure, (s, { error }) => ({ ...s, loading: false, error })),

  on(PreventiviActions.loadDetails, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.loadDetailsSuccess, (s, { item }) => ({ ...s, opLoading: false, selected: item })),
  on(PreventiviActions.loadDetailsFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.create, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.createSuccess, (s) => ({ ...s, opLoading: false, createSuccessCount: s.createSuccessCount + 1 })),
  on(PreventiviActions.createFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.update, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.updateSuccess, (s) => ({ ...s, opLoading: false })),
  on(PreventiviActions.updateFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.delete, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.deleteSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    items: s.items.filter((x) => toId(x.id) !== toId(id)),
    selected: toId(s.selected?.id) === toId(id) ? null : s.selected,
  })),
  on(PreventiviActions.deleteFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.archive, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.archiveSuccess, (s) => ({ ...s, opLoading: false })),
  on(PreventiviActions.archiveFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.unarchive, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.unarchiveSuccess, (s) => ({ ...s, opLoading: false })),
  on(PreventiviActions.unarchiveFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.setAsRefused, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.setAsRefusedSuccess, (s) => ({ ...s, opLoading: false })),
  on(PreventiviActions.setAsRefusedFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.associateToCantiere, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.associateToCantiereSuccess, (s) => ({ ...s, opLoading: false })),
  on(PreventiviActions.associateToCantiereFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.duplicate, (s) => ({ ...s, opLoading: true, opError: null })),
  on(PreventiviActions.duplicateSuccess, (s) => ({ ...s, opLoading: false })),
  on(PreventiviActions.duplicateFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(PreventiviActions.loadDocumenti, state => ({
    ...state,
    documentiLoading: true,
  })),

  on(PreventiviActions.loadDocumentiSuccess, (state, { items }) => ({
    ...state,
    documenti: items,
    documentiLoading: false,
  })),

  on(PreventiviActions.loadDocumentiFailure, state => ({
    ...state,
    documentiLoading: false,
  })),

  on(PreventiviActions.loadCapitoli, state => ({
    ...state,
    capitoliLoading: true,
  })),

  on(PreventiviActions.loadCapitoliSuccess, (state, { items }) => ({
    ...state,
    capitoli: items,
    capitoliLoading: false,
  })),

  on(PreventiviActions.loadCapitoliFailure, state => ({
    ...state,
    capitoliLoading: false,
  })),
  );
